import { observable, makeObservable } from 'mobx';
import { Axios } from '../utilities/network';
import RootStore from './root-store';
import { CURRENT_PLATFORM } from '../constants/settings';

export default class PermissionRoleStore {
	loading: boolean = false;
	loadingInfo: boolean = false;
	creating: boolean = false;
	updating: boolean = false;
	deleting: boolean = false;

	rootStore: RootStore;

	constructor(rootStore: RootStore) {
		makeObservable(this, {
			loading: observable,
			loadingInfo: observable,
			creating: observable,
			updating: observable,
			deleting: observable,
		});
		this.rootStore = rootStore;
	}

	// For the permission checkbox list — every permission the current
	// platform defines, grouped by moduleId/moduleName for display.
	searchPermissions = async (reqModel: any = {}) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Permission/Search?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	// clinicId is passed explicitly (rather than relying on the backend to
	// infer it from the authenticated session) to match how every other
	// clinic-scoped Search endpoint in this app is called — confirm with
	// backend if this turns out to be unnecessary or wrongly named.
	searchRoles = async (skip: number = 0, take: number = 0, reqModel: any = {}) => {
		this.loading = true;
		try {
			const res = await Axios.post(`/api/Permission/Role/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loading = false;
		}
	};

	// Response embeds full `permissions: PermissionInfo[]` — see
	// GetRolePermissionIds in utilities/permission-role.ts for normalizing
	// that into a plain id list.
	getRoleById = async (id: number) => {
		this.loadingInfo = true;
		try {
			const res = await Axios.get(`/api/Permission/Role/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.loadingInfo = false;
		}
	};

	// reqModel must carry `clinicId` (the current clinic's own) — a clinic
	// role created without it would be indistinguishable from a system role.
	// Never called with a bare/empty clinicId; see the page-level guard.
	createRole = async (reqModel: any) => {
		this.creating = true;
		try {
			const res = await Axios.post(`/api/Permission/Role?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.creating = false;
		}
	};

	updateRole = async (reqModel: any) => {
		this.updating = true;
		try {
			const res = await Axios.put(`/api/Permission/Role?platformId=${CURRENT_PLATFORM}`, reqModel);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.updating = false;
		}
	};

	deleteRole = async (id: number) => {
		this.deleting = true;
		try {
			const res = await Axios.delete(`/api/Permission/Role/${id}?platformId=${CURRENT_PLATFORM}`);
			return Promise.resolve(res.data);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			this.deleting = false;
		}
	};
}
